import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack,
  alpha
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingIcon,
  Psychology as AIIcon,
  Group as PeopleIcon,
  Timeline as TimelineIcon,
  Article as NewsIcon,
  Assessment as InsightsIcon,
  Compare as CompareIcon,
  DataUsage as DataIcon,
  Speed as SpeedIcon,
  Lightbulb as LightbulbIcon,
  Launch as LaunchIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

import civiqLogo from '../img/civiq.jpg';

// Mock data for demo charts
const demoConstituencyData = [
  { name: 'BJP', value: 45, color: '#FF6B35' },
  { name: 'INC', value: 30, color: '#19A7CE' },
  { name: 'AAP', value: 15, color: '#F4CE14' },
  { name: 'Others', value: 10, color: '#45474B' }
];

const demoSentimentData = [
  { issue: 'Employment', positive: 65, negative: 35 },
  { issue: 'Healthcare', positive: 72, negative: 28 },
  { issue: 'Infrastructure', positive: 58, negative: 42 },
  { issue: 'Education', positive: 69, negative: 31 }
];

const LandingPage = ({ onSignInClick, onNavigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <AnalyticsIcon />,
      title: "Constituency Intelligence",
      description: "Deep demographics, historical election data, and comprehensive voter profiles for every constituency in India.",
      color: '#1976d2'
    },
    {
      icon: <NewsIcon />,
      title: "Real-Time Media Insights",
      description: "Track trending news, social media buzz, and sentiment analysis across digital platforms in real-time.",
      color: '#388e3c'
    },
    {
      icon: <AIIcon />,
      title: "AI Predictions",
      description: "Advanced AI models provide win probabilities, scenario modeling, and impact analysis for strategic decisions.",
      color: '#7b1fa2'
    },
    {
      icon: <CompareIcon />,
      title: "Candidate & Party Tracking",
      description: "Monitor candidate strengths, party performance, and public perception through comprehensive analytics.",
      color: '#d32f2f'
    }
  ];

  const howItWorks = [
    {
      step: "01",
      icon: <DataIcon />,
      title: "Connect Data",
      description: "CivIQ automatically ingests election data, news feeds, and social media trends from verified sources.",
      color: '#1976d2'
    },
    {
      step: "02",
      icon: <SpeedIcon />,
      title: "Analyze",
      description: "Our AI models detect patterns, analyze sentiment, and generate predictive insights in real-time.",
      color: '#388e3c'
    },
    {
      step: "03",
      icon: <LightbulbIcon />,
      title: "Act",
      description: "Get actionable insights and make data-driven decisions with comprehensive reports and alerts.",
      color: '#f57c00'
    }
  ];

  const testimonials = [
    {
      name: "Dr. Priya Sharma",
      role: "Senior Political Analyst",
      organization: "Centre for Policy Research",
      quote: "CivIQ has revolutionized how we analyze constituency dynamics. The AI predictions are remarkably accurate and the real-time insights are invaluable.",
      avatar: "PS"
    },
    {
      name: "Rajesh Kumar",
      role: "Campaign Manager",
      organization: "Democratic Alliance",
      quote: "The sentiment analysis and media tracking features helped us adjust our strategy in real-time. CivIQ is a game-changer for modern campaigns.",
      avatar: "RK"
    },
    {
      name: "Anita Verma",
      role: "Political Journalist",
      organization: "National Herald",
      quote: "As a journalist covering elections, CivIQ provides the depth of analysis I need to write informed stories. The data visualization is exceptional.",
      avatar: "AV"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      {/* Navigation Header */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          backgroundColor: 'rgba(255,255,255,0.95)', 
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center">
            <Avatar
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 45,
                height: 45,
                borderRadius: 2,
                mr: 2,
                border: '2px solid #1976d2',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
            <Typography 
              variant="h5" 
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                color: 'primary.main',
                background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              CivIQ
            </Typography>
          </Box>

          {!isMobile ? (
            <Box display="flex" alignItems="center" gap={3}>
              <Button 
                color="inherit" 
                onClick={() => scrollToSection('features')}
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                Features
              </Button>
              <Button 
                color="inherit" 
                onClick={() => scrollToSection('how-it-works')}
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                How It Works
              </Button>
              <Button 
                color="inherit" 
                onClick={() => scrollToSection('insights')}
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                Insights
              </Button>
              <Button
                variant="contained"
                onClick={onSignInClick}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1
                }}
              >
                Sign In
              </Button>
            </Box>
          ) : (
            <IconButton 
              color="primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            opacity: 0.4
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box>
                <Chip
                  label="Your AI Campaign Strategist"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    mb: 3,
                    backdropFilter: 'blur(10px)'
                  }}
                />
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 'bold',
                    color: 'white',
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2
                  }}
                >
                  Smarter Political Decisions, {' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Powered by AI
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.5
                  }}
                >
                  CivIQ is the next-generation political intelligence platform for India. We combine real-time election data, social media trends, and news analysis to help campaign teams not just understand voter sentiment, but act on it.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => scrollToSection('demo')}
                    sx={{
                      backgroundColor: '#FF6B35',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      '&:hover': {
                        backgroundColor: '#E55A2B'
                      }
                    }}
                    endIcon={<LaunchIcon />}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => scrollToSection('insights')}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    View Demo
                  </Button>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '& .india-map': {
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))'
                  }
                }}
              >
                {/* Placeholder for India map with data points */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary.main">
                    Live Constituency Data
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <LocationIcon color="success" />
                    <Typography variant="body1" fontWeight={500}>
                      543 Constituencies Tracked
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        92%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Data Accuracy
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        24/7
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Real-time Updates
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" fontWeight="bold" color="primary.main" gutterBottom>
              Comprehensive Political Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Our Vision: To empower political campaigns in India with the intelligence, agility, and foresight of AI. CivIQ bridges the gap between raw data and real-world action.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: `2px solid ${alpha(feature.color, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${alpha(feature.color, 0.15)}`,
                      borderColor: feature.color
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        backgroundColor: feature.color,
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: 10, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" fontWeight="bold" color="primary.main" gutterBottom>
              How CivIQ Works
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Three simple steps to unlock powerful political insights
            </Typography>
          </Box>
          
          <Grid container spacing={6} alignItems="center">
            {howItWorks.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box textAlign="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.8)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      position: 'relative',
                      '&::before': {
                        content: `"${step.step}"`,
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#FF6B35',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }
                    }}
                  >
                    {React.cloneElement(step.icon, { sx: { color: 'white', fontSize: 36 } })}
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.6}>
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Sample Insights Preview */}
      <Box id="insights" sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" fontWeight="bold" color="primary.main" gutterBottom>
              Live Political Insights
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              See how CivIQ transforms complex political data into actionable insights
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: alpha('#1976d2', 0.1),
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%)'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Party Strength Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Real-time tracking of party performance across constituencies
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={demoConstituencyData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name} ${value}%`}
                    >
                      {demoConstituencyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: alpha('#388e3c', 0.1),
                  background: 'linear-gradient(135deg, rgba(56, 142, 60, 0.02) 0%, rgba(56, 142, 60, 0.05) 100%)'
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Issue Sentiment Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  AI-powered sentiment analysis on key political issues
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={demoSentimentData}>
                    <XAxis dataKey="issue" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="positive" fill="#4caf50" name="Positive %" />
                    <Bar dataKey="negative" fill="#f44336" name="Negative %" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 10, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" fontWeight="bold" color="primary.main" gutterBottom>
              Trusted by Political Professionals
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              See what leading analysts and campaign managers say about CivIQ
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: alpha('#1976d2', 0.1),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                      ))}
                    </Box>
                    <Typography variant="body1" color="text.secondary" mb={3} fontStyle="italic">
                      "{testimonial.quote}"
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Avatar
                        sx={{
                          backgroundColor: 'primary.main',
                          mr: 2,
                          fontWeight: 'bold'
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {testimonial.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="primary.main" fontWeight={500}>
                          {testimonial.organization}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call-to-Action Section */}
      <Box id="demo" sx={{ py: 10, backgroundColor: 'primary.main', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
              Make Better Political Decisions with Data
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Join hundreds of political professionals who trust CivIQ for accurate, real-time political intelligence
            </Typography>
            
            <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Grid item>
                <Box display="flex" alignItems="center">
                  <CheckIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="body1">Real-time Updates</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box display="flex" alignItems="center">
                  <CheckIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="body1">AI-Powered Predictions</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box display="flex" alignItems="center">
                  <CheckIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="body1">Comprehensive Analytics</Typography>
                </Box>
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={onSignInClick}
                sx={{
                  backgroundColor: '#FF6B35',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#E55A2B'
                  }
                }}
              >
                Request a Demo
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => scrollToSection('features')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Learn More
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}
                >
                  {/* <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} /> */}
                  <Box
                                component="img"
                                src={civiqLogo}
                                alt="CivIQ Logo"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  mr: 1,
                                  border: '1px solid rgba(255,255,255,0.3)'
                                }}
                              />
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  CivIQ
                </Typography>
              </Box>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Platform
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('features')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Features
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('pricing')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Pricing
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('api')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      API
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Company
                  </Typography>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('about')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      About
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('contact')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Contact
                    </Button>
                  </Box>
                  <Box>
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => onNavigate('privacy')}
                      sx={{ justifyContent: 'flex-start', p: 0.5 }}
                    >
                      Privacy Policy
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Box textAlign="center">
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2025 CivIQ. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
